import tinycolor, { Instance as TinyColor } from 'tinycolor2'
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { defaultSwatches } from '../colorSelector.constants'
import { Box, Divider, Popover, PopoverProps } from '@mui/material'
import PredefinedSwatches from '../predefinedSwatches/predefinedSwatches'
import useStyles from './styles'
import RecentSwatches from '../recentSwatches/recentSwatches'
import OpacitySlider from '../opacitySlider/opacitySlider'
import CustomColorSelector from '../customColorSelector/customColorSelector'
import LineStylePicker from '../lineStylePicker/lineStylePicker'
import { LineStyleProps } from '../lineStyle.types'
import { useWidgetSettingsMenuPlacement } from '../../widgetDashboard/widgetSettingsMenuContext'


export type ColorPopoverProps = {
    onChange: (color: TinyColor) => void,
    value: TinyColor,
    swatches?: string[],
    recentSwatches?: string[],
    disableSwatches?: boolean
    anchorEl: PopoverProps['anchorEl'],
    onAddSwatch?: (color: TinyColor) => void,
    onClose: () => void
} & LineStyleProps

const ColorPopover: React.FC<ColorPopoverProps> = ({
    value, 
    swatches = defaultSwatches,
    disableSwatches,
    onChange,
    onClose,
    recentSwatches = [],
    anchorEl,
    onAddSwatch,
    lineThickness,
    lineStyle,
    onLineThicknessChange,
    onLineStyleChange,
}) => {

    const {classes} = useStyles()
    const menuPlacement = useWidgetSettingsMenuPlacement('left')
    const paperRef = useRef<HTMLDivElement>(null)
    const predefinedSwatches = useMemo(() => swatches.map(sw => tinycolor(sw)), [swatches])
    const recentSwatchesList = useMemo(() => recentSwatches.map(sw => tinycolor(sw)), [recentSwatches])

    const [customSelectionMode, setCustomSelectionMode] = useState(false);

    useEffect(() => {
        const handleMouseDown = (e: MouseEvent) => {
            if (paperRef.current && !paperRef.current.contains(e.target as Node)) {
                const anchor = anchorEl instanceof Element ? anchorEl : null
                if (anchor && anchor.contains(e.target as Node)) {
                    // Click is on the anchor button — let its own onClick handle the toggle
                    return
                }
                onClose()
            }
        }
        document.addEventListener('mousedown', handleMouseDown)
        return () => document.removeEventListener('mousedown', handleMouseDown)
    }, [onClose, anchorEl])

    const onOpacityChange = useCallback((opacity: number) => {
        onChange(value.clone().setAlpha(opacity))
    }, [value, onChange])

    const onColorSelect = useCallback((color: TinyColor) => {
        onChange(color.clone().setAlpha(value.getAlpha()))
    }, [value, onChange])

    const onAddNewClick = useCallback(() => {
        setCustomSelectionMode(true)
    }, [])

    const addSwatchHandler = useCallback((color: TinyColor) => {
        onAddSwatch && onAddSwatch(color)
        setCustomSelectionMode(false)
    }, [onAddSwatch])
    const closeCustomSelectionHandler = useCallback(() => {
        setCustomSelectionMode(false)
    }, [])

    return (
        <Popover open anchorEl={anchorEl}
            disablePortal={menuPlacement.disablePortal}
            anchorOrigin={menuPlacement.anchorOrigin}
            transformOrigin={menuPlacement.transformOrigin}
            container={menuPlacement.container}
            disableEnforceFocus
            disableScrollLock
            sx={{ pointerEvents: 'none', ...menuPlacement.overlaySx }}
            slotProps={{
                paper: {
                    ref: paperRef,
                    className: classes.container,
                    sx: {
                        marginTop: '4px',
                        pointerEvents: 'all',
                        ...menuPlacement.paperOverlaySx,
                    },
                },
            }}
            onClose={onClose}
        >
            <Box>
                {!customSelectionMode && (
                    <>
                        <PredefinedSwatches value={value} swatches={predefinedSwatches} onColorSelect={onColorSelect}  />
                        <Divider  className={classes.divider} />
                        <RecentSwatches value={value} swatches={recentSwatchesList} onColorSelect={onColorSelect} onAddNewClick={onAddNewClick} onNewSwatchAdded={() => {}} />
                    </>)}
                {customSelectionMode && (
                    <>
                        <CustomColorSelector value={value} onColorSelect={onColorSelect} onAddSwatch={addSwatchHandler} onClose={closeCustomSelectionHandler} />
                    </>
                )}
                <Divider  className={classes.divider} />
                <OpacitySlider value={value} onOpacityChange={onOpacityChange}/>
                {lineThickness !== undefined && onLineThicknessChange && (
                    <>
                        <Divider className={classes.divider} />
                        <LineStylePicker
                            lineThickness={lineThickness}
                            onLineThicknessChange={onLineThicknessChange}
                            lineStyle={lineStyle}
                            onLineStyleChange={onLineStyleChange}
                        />
                    </>
                )}
            </Box>
        </Popover>
    );
}

export default memo(ColorPopover)
